import { ValidationErrors } from 'final-form';
import arrayMutators from 'final-form-arrays';
import React, { ReactNode } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Language } from '~common/i18n';
import {
  CreateSponsorTask,
  createSponsorTaskFactory,
} from '~admin/interactor/sponsor/perform-create-sponsor';
import { MediaData } from '~admin/model/media-data';
import { getTranslationInitialValues } from '~admin/view/components/create-event/helper/create-event-initial-values.helper';
import { getCreateSponsorRequest } from '~admin/view/components/details/sponsor-details/helper/create-sponsor-request.helper';
import { AddButton } from '~admin/view/components/ui/button/add-button.component';
import { MediaField } from '~admin/view/components/ui/form/fields/field-media.component';
import { TextField } from '~common/components/form/field-text.component';
import {
  TranslationField,
  TranslationFormData,
} from '~admin/view/components/ui/form/fields/field-translation.component';
import { MediaType } from '~client/model/media-type';
import { Box } from '~common/components/box.component';
import { FormButton } from '~common/components/button/form-button.component';
import { FormFields } from '~common/components/form/form-fields.component';
import { Span } from '~common/components/span.component';
import { onSuccessReaction } from '~common/helper/mobx-tasks';
import { useModalControls } from '~common/hook/use-modal-controls';
import { useTask } from '~common/hook/use-task';
import { validate, validateArrayField } from '~common/validator';
import { required } from '~common/validator/validations';
import { OverlayProps } from '~web/view/components/layout/overlay.component';
import { TaskOverlay } from '~web/view/components/layout/task-overlay.component';

export interface CreateSponsorFormData {
  sponsorMedia: MediaData;
  url: TranslationFormData<string>[];
}

interface CreateSponsorFormProps extends FormRenderProps<CreateSponsorFormData> {
  task: CreateSponsorTask;
}

const CreateSponsorForm: React.FunctionComponent<CreateSponsorFormProps> = ({
  form,
  handleSubmit,
  invalid,
  task,
}) => {
  const { t } = useTranslation();
  return (
    <FormFields onSubmit={handleSubmit} autoComplete={'off'}>
      <Top>
        <MediaField
          name={'sponsorMedia'}
          label={t('details.sponsor.overlay.media')}
          mediaType={MediaType.IMAGE}
          onMediaSelected={(media: MediaData): void => form.change('sponsorMedia', media)}
        />
      </Top>
      <RestrictBox>
        <TranslationField
          name={'url'}
          label={t('details.sponsor.overlay.url')}
          render={(renderProps): ReactNode => <TextField {...renderProps} />}
        />
      </RestrictBox>
      <ButtonContainer>
        <Box>
          <FormButton task={task} isInvalid={invalid}>
            {t('createEvent.createButton')}
          </FormButton>
        </Box>
      </ButtonContainer>
    </FormFields>
  );
};

const CreateSponsorOverlay: React.FunctionComponent<OverlayProps> = ({ onCancel }) => {
  const { t } = useTranslation();
  const createSponsorTask = useTask(createSponsorTaskFactory);

  onSuccessReaction(createSponsorTask, onCancel);

  const onSubmit = (values: CreateSponsorFormData): void => {
    createSponsorTask(getCreateSponsorRequest(values));
  };

  return (
    <TaskOverlay
      title={t('details.sponsor.overlay.title')}
      onCancel={onCancel}
      task={createSponsorTask}
    >
      <Form
        onSubmit={onSubmit}
        render={(renderProps: FormRenderProps<CreateSponsorFormData>): ReactNode => (
          <CreateSponsorForm task={createSponsorTask} {...renderProps} />
        )}
        validate={(values: CreateSponsorFormData): ValidationErrors | undefined => ({
          sponsorMedia: validate(values.sponsorMedia, [required()]),
          ...validateArrayField(values, 'url', [required()]),
        })}
        mutators={{
          ...arrayMutators,
        }}
        initialValues={{ url: [getTranslationInitialValues(Language.FRENCH)] }}
      />
    </TaskOverlay>
  );
};

export const CreateSponsor: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const [overlayShown, showOverlay, hideOverlay] = useModalControls();

  return (
    <Container>
      <Title>{t('details.sponsor.overlay.title')}</Title>
      <AddButton onClick={showOverlay} />
      {overlayShown && <CreateSponsorOverlay onCancel={hideOverlay} />}
    </Container>
  );
};

const Container = styled(Box)`
  width: 100%;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 2px solid ${(props): string => props.theme.colors.gray.v3};
`;

const Top = styled(Box)`
  width: 100%;
  margin-top: 24px;
`;

const RestrictBox = styled(Box)`
  flex: 1 1 auto;
  margin-top: 24px;
`;

const Title = styled(Span)`
  font-weight: bold;
  font-size: 22px;
  line-height: 22px;
`;

const ButtonContainer = styled(Box)`
  width: 100%;
  justify-content: flex-end;
  margin-top: 32px;
`;



// WEBPACK FOOTER //
// ./view/components/details/sponsor-details/create-sponsor.component.tsx